/**
 * VoiceAssistantOnboarding — Mini-onboarding 3 slides montré une seule fois
 * au premier passage de l'utilisateur sur son dashboard.
 *
 * Stockage de l'état "vu" dans localStorage. Dismissible.
 */
import { useEffect, useState } from "react";
import { X, Phone, ShieldCheck, FileText, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import PremiumPhoneButton from "@/components/PremiumPhoneButton";

const STORAGE_KEY = "confilex_voice_onboarding_seen";

const SLIDES = [
  {
    icon: Phone,
    title: "Un assistant vocal qui vous appelle",
    body: "Cliquez sur le bouton doré et notre assistant vocal CONFILEX vous rappelle dans quelques instants. Ton calme et institutionnel, expérience proche d'une conciergerie premium.",
    accent: "Saisissez simplement votre numéro.",
  },
  {
    icon: ShieldCheck,
    title: "Respect total du RGPD",
    body: "Avant toute transcription, l'assistant demande votre consentement vocal. Vous pouvez refuser à tout moment — l'appel se poursuit sans enregistrement.",
    accent: "Droit à l'effacement disponible à tout moment.",
  },
  {
    icon: FileText,
    title: "Une synthèse exploitable",
    body: "À la fin de l'appel, vous recevez une synthèse structurée : chronologie, faits clés, urgence, documents à fournir. Vous la corrigez, validez, puis la transmettez au professionnel compétent.",
    accent: "Préqualification haut de gamme.",
  },
];

export default function VoiceAssistantOnboarding() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [phoneOpen, setPhoneOpen] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const t = setTimeout(() => setOpen(true), 1500);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, []);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
  };

  const launch = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setPhoneOpen(true);
  };

  if (!open && !phoneOpen) return null;

  if (phoneOpen) {
    return (
      <PremiumPhoneButton
        externalOpen={phoneOpen}
        onExternalClose={() => setPhoneOpen(false)}
      />
    );
  }

  const slide = SLIDES[step];
  const Icon = slide.icon;
  const isLast = step === SLIDES.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#031B12]/60 backdrop-blur-sm p-4"
      data-testid="voice-onboarding"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-[#031B12] shadow-2xl border border-[#F5C400]/30 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-[#031B12] to-[#063D25] text-white px-6 py-5">
          <button
            onClick={close}
            data-testid="voice-onboarding-close"
            className="absolute top-4 right-4 text-white/50 hover:text-white"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-3.5 h-3.5 text-[#F5C400]" />
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#F5C400]">Nouveau · Service premium</p>
          </div>
          <h2 className="font-serif italic text-2xl">Découvrez l'assistant vocal CONFILEX</h2>
        </div>

        <div className="px-6 py-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#F5C400]/10 mx-auto flex items-center justify-center mb-5">
            <Icon className="w-6 h-6 text-[#F5C400]" />
          </div>
          <h3 className="font-serif italic text-lg text-[#031B12] mb-3" data-testid="voice-onboarding-title">
            {slide.title}
          </h3>
          <p className="text-sm text-[#BFC8C2] leading-relaxed mb-3">
            {slide.body}
          </p>
          <p className="text-xs text-[#F5C400] italic">{slide.accent}</p>
        </div>

        <div className="px-6 pb-6">
          <div className="flex items-center justify-center gap-1.5 mb-5">
            {SLIDES.map((_, i) => (
              <button
                key={`dot-${i}`}
                onClick={() => setStep(i)}
                aria-label={`Étape ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-6 bg-[#F5C400]" : "w-1.5 bg-slate-300 hover:bg-slate-400"
                }`}
              />
            ))}
          </div>
          <div className="flex justify-between gap-3">
            <button
              onClick={() => step === 0 ? close() : setStep(step - 1)}
              data-testid="voice-onboarding-prev"
              className="text-xs text-[#BFC8C2]/85 hover:text-[#031B12] inline-flex items-center gap-1"
            >
              {step === 0 ? "Plus tard" : <><ChevronLeft className="w-3 h-3" /> Précédent</>}
            </button>
            {!isLast ? (
              <button
                onClick={() => setStep(step + 1)}
                data-testid="voice-onboarding-next"
                className="px-4 py-2 rounded-lg bg-[#031B12] text-white text-sm hover:brightness-110 inline-flex items-center gap-1"
              >
                Suivant <ChevronRight className="w-3 h-3" />
              </button>
            ) : (
              <button
                onClick={launch}
                data-testid="voice-onboarding-launch"
                className="px-4 py-2 rounded-lg bg-[#F5C400] text-[#031B12] font-medium text-sm hover:brightness-105 inline-flex items-center gap-1.5"
              >
                <Phone className="w-3.5 h-3.5" /> Essayer maintenant
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
